import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import API from "../services/api";

const packageData = [
  { name: "Basic",   value: 10, color: "#3b82f6" },
  { name: "Classic", value: 7,  color: "#8b5cf6" },
  { name: "Advance", value: 5,  color: "#f59e0b" },
  { name: "Premium", value: 3,  color: "#10b981" },
];

const activityData = [
  { month: "Jan", deals: 12, revenue: 4.2 },
  { month: "Feb", deals: 18, revenue: 6.1 },
  { month: "Mar", deals: 14, revenue: 5.3 },
  { month: "Apr", deals: 22, revenue: 8.7 },
  { month: "May", deals: 19, revenue: 7.4 },
  { month: "Jun", deals: 27, revenue: 10.2 },
];

/* ── palette ── */
const T = {
  page:        "#f0f4f8",
  card:        "#ffffff",
  cardBorder:  "#e2e8f0",
  titleText:   "#0f172a",
  bodyText:    "#334155",
  mutedText:   "#64748b",
  hintText:    "#94a3b8",
  divider:     "#e2e8f0",
  inputBg:     "#f8fafc",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0",
      borderRadius: 10, padding: "10px 16px",
      fontSize: 13, color: T.bodyText,
      boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
    }}>
      <p style={{ margin: "0 0 4px", color: T.mutedText, fontWeight: 600 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: 0, color: p.color, fontWeight: 600 }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

function StatCard({ icon, label, value, sub, accent, delta }) {
  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.cardBorder}`,
      borderRadius: 16,
      padding: "20px 22px",
      display: "flex", flexDirection: "column", gap: 12,
      position: "relative", overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    }}>
      <div style={{
        position: "absolute", top: 0, right: 0,
        width: 90, height: 90,
        background: `radial-gradient(circle at top right, ${accent}18, transparent 70%)`,
        borderRadius: "0 16px 0 100%",
      }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: `${accent}15`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18,
        }}>{icon}</div>
        {delta !== undefined && (
          <span style={{
            fontSize: 12, fontWeight: 600,
            color: delta >= 0 ? "#059669" : "#dc2626",
            background: delta >= 0 ? "#d1fae5" : "#fee2e2",
            padding: "3px 8px", borderRadius: 20,
          }}>
            {delta >= 0 ? "↑" : "↓"} {Math.abs(delta)}%
          </span>
        )}
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 12, color: T.mutedText, fontWeight: 500 }}>{label}</p>
        <p style={{ margin: "4px 0 0", fontSize: 26, fontWeight: 700, color: T.titleText, letterSpacing: "-0.5px" }}>{value}</p>
      </div>
      {sub && <p style={{ margin: 0, fontSize: 12, color: accent, fontWeight: 600 }}>{sub}</p>}
    </div>
  );
}

function ITDashboard() {
  const [deals, setDeals]       = useState([]);
  const [users, setUsers]       = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    API.get("/deals").then(r => setDeals(r.data)).catch(() => {});
    API.get("/users").then(r => setUsers(r.data)).catch(() => {});
    API.get("/services").then(r => setServices(r.data)).catch(() => {});
  }, []);

  const totalRevenue   = deals.reduce((s, d) => s + Number(d.total_amount  || 0), 0);
  const totalPending   = deals.reduce((s, d) => s + Number(d.pending_amount || 0), 0);
  const activeServices = services.filter(s => s.status === "active").length;
  const recentDeals    = [...deals].slice(-5).reverse();

  const packageDist = ["Basic","Classic","Advance","Premium"].map((name, i) => ({
    name,
    value: deals.filter(d => d.package_name === name).length || packageData[i].value,
    color: packageData[i].color,
  }));

  const cardBox = {
    background: T.card,
    border: `1px solid ${T.cardBorder}`,
    borderRadius: 16,
    padding: "22px 24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: T.page,
      color: T.bodyText,
      fontFamily: "'Inter', -apple-system, sans-serif",
      padding: "28px 32px",
    }}>

      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            {/* <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 15, fontWeight: 800, color: "#fff",
            }}>D</div> */}
            {/* <span style={{ fontSize: 13, color: T.mutedText, fontWeight: 700, letterSpacing: "0.07em" }}>DIGIMARK CRM</span> */}
          </div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: T.titleText, letterSpacing: "-0.3px" }}>IT Manager Dashboard</h1>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: T.mutedText }}>System overview and performance metrics</p>
        </div>
        {/* <div style={{ display: "flex", gap: 10 }}> */}
          {/* <button style={{
            background: T.card, border: `1px solid ${T.cardBorder}`,
            color: T.bodyText, padding: "9px 18px", borderRadius: 10,
            fontSize: 13, cursor: "pointer", fontWeight: 500,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}>↻ Refresh</button> */}
          {/* <div style={{
            background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
            padding: "9px 20px", borderRadius: 10,
            fontSize: 13, cursor: "pointer", fontWeight: 600, color: "#fff",
            boxShadow: "0 4px 12px rgba(59,130,246,0.30)",
          }}>+ Add user</div>
        </div> */}
      </div>

      {/* KPI ROW 1 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 16 }}>
        <StatCard icon="📦" label="Total deals"      value={deals.length || 25}   sub="All packages combined"  accent="#3b82f6" delta={12} />
        <StatCard icon="⚙️" label="Active services"  value={activeServices || 18}  sub="Running this month"    accent="#10b981" delta={4}  />
        <StatCard icon="👥" label="Team members"     value={users.length || 18}    sub="Across all roles"      accent="#8b5cf6" delta={0}  />
        <StatCard icon="⚡" label="Pending amount"   value={`₹${(totalPending/1000).toFixed(0)||"48"}K`} sub="Awaiting collection" accent="#f59e0b" delta={-3} />
      </div>

      {/* KPI ROW 2 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        <StatCard icon="💰" label="Total revenue"    value={`₹${(totalRevenue/100000).toFixed(1)||"21.3"}L`} sub="Contracted value" accent="#10b981" delta={18} />
        <StatCard icon="🎯" label="Hot leads"        value={deals.filter(d=>d.category==="Hot").length||9}   sub="High priority"    accent="#ef4444" />
        <StatCard icon="📊" label="Packages"         value={new Set(deals.map(d=>d.package_name)).size||4}   sub="Active tiers"     accent="#f59e0b" />
        <StatCard icon="🔧" label="Open tickets"     value={6}                                                sub="Needs attention"  accent="#3b82f6" delta={-2} />
      </div>

      {/* CHARTS ROW */}
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20, marginBottom: 20 }}>

        {/* AREA CHART */}
        <div style={cardBox}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.titleText }}>Revenue trend</h3>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: T.mutedText }}>Monthly overview — last 6 months</p>
            </div>
            <div style={{
              background: "#eff6ff", border: "1px solid #bfdbfe",
              borderRadius: 8, padding: "4px 12px", fontSize: 12, color: "#3b82f6", fontWeight: 700,
            }}>Lakhs ₹</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={activityData}>
              <defs>
                <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}    />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" tick={{ fill: T.mutedText, fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis                  tick={{ fill: T.mutedText, fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="revenue" name="Revenue (L)" stroke="#3b82f6" strokeWidth={2.5} fill="url(#grad1)" dot={{ fill: "#3b82f6", r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* PIE CHART */}
        <div style={cardBox}>
          <div style={{ marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.titleText }}>Package split</h3>
            <p style={{ margin: "3px 0 0", fontSize: 12, color: T.mutedText }}>Distribution by tier</p>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={packageDist} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                {packageDist.map((entry, i) => (
                  <Cell key={i} fill={entry.color} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", marginTop: 8 }}>
            {packageDist.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
                <span style={{ color: T.mutedText }}>{p.name}</span>
                <span style={{ color: T.titleText, fontWeight: 700, marginLeft: "auto" }}>{p.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* BAR + RECENT DEALS */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 20 }}>

        {/* BAR */}
        <div style={cardBox}>
          <div style={{ marginBottom: 18 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.titleText }}>Monthly deals</h3>
            <p style={{ margin: "3px 0 0", fontSize: 12, color: T.mutedText }}>Count by month</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={activityData} barSize={20}>
              <XAxis dataKey="month" tick={{ fill: T.mutedText, fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis                  tick={{ fill: T.mutedText, fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="deals" name="Deals" fill="#8b5cf6" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* RECENT DEALS */}
        <div style={cardBox}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.titleText }}>Recent deals</h3>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: T.mutedText }}>Latest activity</p>
            </div>
            <span style={{ fontSize: 12, color: "#3b82f6", cursor: "pointer", fontWeight: 600 }}>View all →</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(recentDeals.length ? recentDeals : [
              { business_name: "Tech Corp",    package_name: "Premium", total_amount: 85000, category: "Hot"  },
              { business_name: "Sunrise Pvt",  package_name: "Classic", total_amount: 45000, category: "Warm" },
              { business_name: "BluePeak Ltd", package_name: "Advance", total_amount: 70000, category: "Hot"  },
              { business_name: "Nexus Media",  package_name: "Basic",   total_amount: 25000, category: "Cold" },
              { business_name: "Orbit Digital",package_name: "Classic", total_amount: 45000, category: "Warm" },
            ]).slice(0,5).map((d, i) => {
              const catColor = d.category === "Hot" ? "#ef4444" : d.category === "Warm" ? "#f59e0b" : "#3b82f6";
              const catBg    = d.category === "Hot" ? "#fee2e2" : d.category === "Warm" ? "#fef3c7" : "#dbeafe";
              const pkgColor = { Premium:"#10b981", Classic:"#8b5cf6", Advance:"#f59e0b", Basic:"#3b82f6" }[d.package_name] || T.mutedText;
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 14px", borderRadius: 10,
                  background: T.inputBg, border: `1px solid ${T.cardBorder}`,
                }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 8,
                    background: `${pkgColor}18`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 700, color: pkgColor,
                  }}>{d.business_name?.charAt(0)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.titleText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {d.business_name}
                    </p>
                    <p style={{ margin: 0, fontSize: 11, color: T.mutedText }}>{d.package_name} package</p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.titleText }}>
                      ₹{Number(d.total_amount).toLocaleString("en-IN")}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: catColor,
                      background: catBg, padding: "2px 8px", borderRadius: 20,
                    }}>{d.category}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

export default ITDashboard;